import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import { TransferNotification } from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';

import {
  Transfer,
  TransferStatus,
  TransfersResp,
  getTransfers,
} from '#/api/transfers';
import { useWalletView } from '#/features/wallet/wallet-context';
import { publishNotification } from '#/features/notifications/topic';

import { transferView, TransferView } from './transfer-view';
import { depositToastView, withdrawalToastView } from './toast-views';

const adapter = createEntityAdapter<Transfer>({
  sortComparer: (a, b) =>
    b.id.localeCompare(a.id, undefined, { numeric: true }),
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: TransfersState = {
  status: 'connected',
  error: '',
  transfers: adapter.getInitialState(),
};

export interface TransfersState extends LiveConnectionBaseState {
  readonly transfers: EntityState<Transfer>;
}

export interface TransfersView extends Omit<TransfersState, 'transfers'> {
  readonly transfers: readonly TransferView[];
  readonly activeCount: number;
}

export const [useTransfersView, TransfersViewCtx] =
  createSafeContext<TransfersView>('TransfersView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function TransfersProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);
  const view = useMemo(() => createView(state), [state]);
  useTransfersFetch(setState);
  return <TransfersViewCtx value={view}>{props.children}</TransfersViewCtx>;
}

export function createView(state: TransfersState): TransfersView {
  const transfers = selectors
    .selectAll(state.transfers)
    .map((transfer) => transferView(transfer));
  const activeCount = activeCountView(state);

  return { ...state, transfers, activeCount };
}

const ACTIVE_STATUS: Set<TransferStatus> = new Set(['PENDING', 'AVAILABLE']);
function activeCountView(state: TransfersState) {
  const transfers = selectors.selectAll(state.transfers);
  const activeTransfers = transfers.filter((transfer) =>
    ACTIVE_STATUS.has(transfer.status),
  );
  return activeTransfers.length;
}

function useTransfersFetch(
  setState: Dispatch<SetStateAction<TransfersState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;

  const channel = isReady ? 'transfers' : '';

  const fetchAll = useCallback(
    async (signal: AbortSignal) => getTransfers({ signal }),
    [],
  );

  const setAll = useCallback(
    (data: TransfersResp) => {
      setState((state) => ({
        ...state,
        transfers: adapter.setAll(state.transfers, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      transfers: adapter.removeAll(state.transfers),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: TransferNotification) => {
      const transfer = notif.data;

      switch (transfer.kind) {
        case 'DEPOSIT':
          switch (transfer.status) {
            case 'AVAILABLE':
              publishNotification({
                kind: 'deposit-request-ready',
                props: depositToastView(transfer),
              });
              break;
            case 'COMPLETED':
              publishNotification({
                kind: 'deposit-successful',
                props: depositToastView(transfer),
              });
              break;
            case 'PENDING':
            case 'FAILED':
              // Left to be managed in the components layer as it's not easy to guarantee
              // that only one toast, not more, not less, will be fired per update.
              break;
            // no default
          }
          break;
        case 'WITHDRAWAL':
          switch (transfer.status) {
            case 'PENDING':
              publishNotification({
                kind: 'initiate-withdraw-request-in-progress',
                props: withdrawalToastView(transfer),
              });
              break;
            case 'AVAILABLE':
              publishNotification({
                kind: 'withdraw-request-ready',
                props: withdrawalToastView(transfer),
              });
              break;
            case 'COMPLETED':
              publishNotification({
                kind: 'withdraw-successful',
                props: withdrawalToastView(transfer),
              });
              break;
            case 'FAILED':
              // Left to be managed in the components layer as it's not easy to guarantee
              // that only one toast, not more, not less, will be fired per update.
              break;
            // no default
          }
        // no default
      }

      setState((state) => ({
        ...state,
        transfers: adapter.setOne(state.transfers, transfer),
      }));
    },
    [setState],
  );

  useLiveConnection<TransfersResp, TransfersState, TransferNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
